import {useCallback} from 'react';
import {useSelector} from 'react-redux';
import {
  useDeleteCommentMutation,
  useEditCommentMutation,
  useGetPostsQuery,
  useReplyMutation,
  useToggleLikeMutation,
} from '~/ViewInvitation/api/endpoints/posts';
import {useCurrentGuestId} from '~/ViewInvitation/components/Guestlist/hooks/useCurrentGuestId';
import {selectEventId} from '~/ViewInvitation/selectors/eventDetails';
import type {Post} from '~/ViewInvitation/types/api';

const TOGGLE_LIKE_SHARED_CACHE_KEY = 'shared-toggle-like-key';
const REPLY_SHARED_CACHE_KEY = 'shared-reply-key';
const DELETE_COMMENT_SHARED_CACHE_KEY = 'shared-delete-comment-key';
const EDIT_COMMENT_SHARED_CACHE_KEY = 'shared-edit-comment-key';

export const usePostsApi = () => {
  const {guestId} = useCurrentGuestId();
  const eventId = useSelector(selectEventId);
  const {data: posts = [], refetch, ...getPosts} = useGetPostsQuery({eventId, guestId});
  const [doToggleLike, toggleLikeStatus] = useToggleLikeMutation({
    fixedCacheKey: TOGGLE_LIKE_SHARED_CACHE_KEY,
  });
  const [doReply, replyStatus] = useReplyMutation({
    fixedCacheKey: REPLY_SHARED_CACHE_KEY,
  });
  const [doDeleteComment, deleteCommentStatus] = useDeleteCommentMutation({
    fixedCacheKey: DELETE_COMMENT_SHARED_CACHE_KEY,
  });
  const [doEditComment, editCommentStatus] = useEditCommentMutation({
    fixedCacheKey: EDIT_COMMENT_SHARED_CACHE_KEY,
  });

  const getPostsForGuest = useCallback(
    (userId: string, postType: string = 'rsvp'): Post[] | undefined => {
      return posts
        .filter((post) => post.creator.id === userId && post.postType === postType)
        .sort((a, b) => {
          // data format "20251009T230208"
          const aDate = a.created.replace('T', '');
          const bDate = b.created.replace('T', '');
          if (aDate === bDate) return 0;
          // most recent first
          return aDate > bDate ? -1 : 1;
        });
    },
    [posts]
  );

  const toggleLike = useCallback(
    async (postId: number) => doToggleLike({eventId, postId, guestId}).unwrap(),
    [doToggleLike, eventId, guestId]
  );

  const reply = useCallback(
    async ({comment, postId}: {comment: string; postId: number}) => {
      if (!comment) throw new Error('Comment is required');
      return doReply({eventId, postId, comment, guestId}).unwrap();
    },
    [doReply, eventId, guestId]
  );

  const deleteComment = useCallback(
    async (postId: number) => doDeleteComment({eventId, postId, guestId}).unwrap(),
    [doDeleteComment, eventId, guestId]
  );

  const editComment = useCallback(
    async ({comment, postId}: {comment: string; postId: number}) => {
      if (!comment) throw new Error('Comment is required');
      return doEditComment({eventId, postId, comment, guestId}).unwrap();
    },
    [doEditComment, eventId, guestId]
  );

  const isLoading = getPosts.isLoading || toggleLikeStatus.isLoading || replyStatus.isLoading;
  const isFetching = getPosts.isFetching;

  return {
    posts,
    isFetching,
    isLoading,
    isReplying: replyStatus.isLoading,
    isDeleting: deleteCommentStatus.isLoading,
    getPostsForGuest,
    toggleLike,
    reply,
    deleteComment,
    editComment,
    refetchPosts: refetch,
    status: {
      getPosts,
      toggleLike: toggleLikeStatus,
      reply: replyStatus,
      deleteComment: deleteCommentStatus,
      editComment: editCommentStatus,
    },
  };
};
